import React, { useState, useEffect } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify"; // Import Toastify
import "react-toastify/dist/ReactToastify.css";
import BASE_URL from "../config";
import Loader from "../components/Loader"; // Import Loader component

const Users = () => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("id");
  const [sortOrder, setSortOrder] = useState("desc"); // Default to 'desc' for ID
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [userType, setUserType] = useState("");
  const [loading, setLoading] = useState(true); // Loading state
  const [loggedInUserRole, setLoggedInUserRole] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      window.location.href = "/login";
      return;
    }

    // Fetch logged-in user details
    const fetchLoggedInUser = async () => {
      try {
        const { data } = await axios.get(`${BASE_URL}api/user/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setLoggedInUserRole(data.role);
      } catch (error) {
        toast.error("Failed to fetch user details");
      }
    };

    fetchLoggedInUser();
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true); // Show loader while fetching data
      try {
        const token = localStorage.getItem("token");
        const { data } = await axios.get(`${BASE_URL}api/user/order`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUsers(data);
      } catch (error) {
        toast.error("Failed to fetch Users");
      } finally {
        setLoading(false); // Hide loader after data is fetched
      }
    };
    fetchUsers();
  }, []);

  const handleSearch = (e) => setSearch(e.target.value);
  const handleSort = (field) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(order);
  };

  const handleUpdateUserField = async (userId, field, value) => {
    const token = localStorage.getItem("token");
    try {
      await axios.put(
        `${BASE_URL}api/user/update-user/${userId}`,
        { [field]: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers((prev) =>
        prev.map((user) =>
          user.id === userId ? { ...user, [field]: value } : user
        )
      );
      toast.success(`User ${field} updated`);
    } catch (error) {
      toast.error(`Failed to update user ${field}`);
    }
  };

  const filteredUsers = users.filter((user) =>
    (user.username.toLowerCase().includes(search.toLowerCase()) ||
      user.email.toLowerCase().includes(search.toLowerCase())) &&
    (userType ? user.role === userType : true)
  );

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    if (a[sortField] < b[sortField]) return sortOrder === "asc" ? -1 : 1;
    if (a[sortField] > b[sortField]) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(sortedUsers.length / pageSize);
  const paginatedUsers = sortedUsers.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div className="container mt-4">
      <div className="mb-3 d-flex justify-content-between">
        <h2>Users</h2>
        <a href="add-user" className="btn btn-primary d-flex align-items-center">Add User</a>
      </div>
      <div className="col-md-3">
        <select className="form-select" value={userType} onChange={(e) => setUserType(e.target.value)}>
          <option value="">User role</option>
          <option value="admin">Admin</option>
          <option value="Tester">Tester</option>
          <option value="Software Engineer">Software Engineer</option>
          <option value="user">User</option>
          <option value="manager">Manager</option>
        </select>
      </div>
      <br />
      <div className="row mb-4">
        <div className="col-md-1">
          <select className="form-select" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="20">20</option>
          </select>
        </div>
        <div className="col-md-6">
          <input type="text" className="form-control" placeholder="Search by username or email..." value={search} onChange={handleSearch} />
        </div>
      </div>
      <div className="table-responsive">
        {loading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: "200px" }}>
            <Loader message="Loading users..." /> {/* Show loader only on the table */}
          </div>
        ) : (
          <table className="table table-hover table-striped align-middle">
            <thead className="table-dark">
              <tr>
                <th onClick={() => handleSort("id")}>ID</th>
                {loggedInUserRole === "admin" && <th>Edit</th>}
                <th onClick={() => handleSort("username")}>Name</th>
                <th onClick={() => handleSort("email")}>Email</th>
                <th>Phone</th>
                <th onClick={() => handleSort("role")}>Role</th>
                <th>Status</th>
                <th>Last Login</th>
              </tr>
            </thead>
            <tbody>
              {paginatedUsers.length > 0 ? (
                paginatedUsers.map((user) => (
                  <tr key={user.id}>
                    <td>{user.id}</td>
                    {loggedInUserRole === "admin" && (
                      <td>
                        <a href={`/edit-user?id=${user.id}`} style={{ color: "black" }}>Edit</a>
                      </td>
                    )}
                    <td>
                      <a href={`/user-detail?id=${user.id}`} style={{ color: "blue" }}>
                        {user.username.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}
                      </a>
                    </td>
                    <td>{user.email}</td>
                    <td>{user.phone ?? ""}</td>
                    <td>
                      {loggedInUserRole === "admin" ? (
                        <select
                          className={`form-select form-select-sm
                            ${user.role === "admin" ? "bg-danger text-light" :
                              user.role === "user" ? "bg-primary text-light" :
                                user.role === "manager" ? "bg-warning text-dark" :
                                  user.role === "tester" ? "bg-info text-dark" :
                                    "bg-secondary text-light"}`}
                          value={user.role}
                          onChange={e => handleUpdateUserField(user.id, "role", e.target.value)}
                          style={{ textTransform: "uppercase", minWidth: 100 }}
                        >
                          <option value="admin">ADMIN</option>
                          <option value="tester">TESTER</option>
                          <option value="user">USER</option>
                          <option value="manager">MANAGER</option>
                          <option value="developer">DEVELOPER</option>
                        </select>
                      ) : (
                        <span className={`badge badge-sm 
                          ${user.role === "admin" ? "bg-danger" :
                            user.role === "user" ? "bg-primary" :
                              user.role === "manager" ? "bg-warning" :
                                user.role === "tester" ? "bg-info" :
                                  "bg-secondary"}`}
                          style={{ textTransform: "uppercase" }}>
                          {user.role}
                        </span>
                      )}
                    </td>
                    <td>
                      {loggedInUserRole === "admin" ? (
                        <select
                          className={`form-select form-select-sm
                            ${user.status === "active" ? "bg-success text-light" : "bg-danger text-light"}`}
                          value={user.status}
                          onChange={e => handleUpdateUserField(user.id, "status", e.target.value)}
                          style={{ textTransform: "uppercase", minWidth: 90 }}
                        >
                          <option value="active">ACTIVE</option>
                          <option value="inactive">INACTIVE</option>
                        </select>
                      ) : (
                        <span className={`badge badge-sm ${user.status === "active" ? "bg-success" : "bg-danger"}`} style={{ textTransform: "uppercase" }}>
                          {user.status}
                        </span>
                      )}
                    </td>
                    <td>
                      {user.last_login
                        ? new Date(user.last_login).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
                        : ""}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="11" className="text-center">No users found</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      <div className="d-flex justify-content-center mt-4">
        <button className="btn btn-outline-primary mx-2" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
          Previous
        </button>
        <span className="align-self-center">Page {currentPage} of {totalPages}</span>
        <button className="btn btn-outline-primary mx-2" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
          Next
        </button>
      </div>
      <ToastContainer /> {/* Add ToastContainer for notifications */}
    </div>
  );
};

export default Users;

